import { zodResolver } from '@hookform/resolvers/zod'
import { Modal } from 'antd'
import { useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import AtomCheckbox from '@/components/Atoms/AtomCheckbox/AtomCheckbox'
import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomModalWithForm from '@/components/Atoms/AtomModalWithForm/AtomModalWithForm'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  FormInputControlType,
  FormInputRegisterType,
  SelectFormControlType,
  UnitOfMeasurementInterface,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { fetchUnitsOfMeasurement } from '@/utils/api/fetch/fetchEntities'
import { isCatalogApprover } from '@/utils/catalog/approvalStatus'

import cn from './CharacteristicCreationModal.module.scss'
import {
  canUseUnitOfMeasurement,
  CHARACTERISTIC_CREATION_DEFAULTS,
  characteristicCreationSchema,
  type CharacteristicCreationValues,
  toCreateCharacteristicRequest,
  VALUE_TYPE_OPTIONS,
} from './lib/characteristicCreationForm'

type CreatedCharacteristic = {
  id: string
  code: number
  description: string
  valueType: 'NUMERIC' | 'BOOLEAN' | 'REFERENCE' | 'STRING'
  status: 'DRAFT' | 'APPROVED' | 'REJECTED'
  useUnitOfMeasurement: boolean
  unitOfMeasurementDto?: UnitOfMeasurementInterface | null
}

type CharacteristicCreationModalProps = {
  open: boolean
  setOpen: (open: boolean) => void
  onSuccess?: (created?: CreatedCharacteristic) => void
}

let unitsCache: UnitOfMeasurementInterface[] | null = null
let unitsRequest: Promise<UnitOfMeasurementInterface[]> | null = null

function CharacteristicCreationModal({
  open,
  setOpen,
  onSuccess,
}: CharacteristicCreationModalProps) {
  const [units, setUnits] = useState<UnitOfMeasurementInterface[]>([])
  const [loadingUnits, setLoadingUnits] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [modal, contextHolder] = Modal.useModal()
  const currentUser = useUserInfoStore((state) => state.currentUser)
  const isApprover = isCatalogApprover(currentUser?.roles)

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CharacteristicCreationValues>({
    defaultValues: CHARACTERISTIC_CREATION_DEFAULTS,
    resolver: zodResolver(characteristicCreationSchema),
  })
  const valueType = useWatch({ control, name: 'valueType' })
  const useUnitOfMeasurement = useWatch({
    control,
    name: 'useUnitOfMeasurement',
  })
  const hasUnitOption = canUseUnitOfMeasurement(valueType.value)

  const ensureUnitsLoaded = async (force = false) => {
    if (!force && units.length > 0) return units
    if (!force && unitsCache) {
      setUnits(unitsCache)
      return unitsCache
    }
    if (!force && unitsRequest) {
      setLoadingUnits(true)
      try {
        const cachedUnits = await unitsRequest
        setUnits(cachedUnits)
        return cachedUnits
      } finally {
        setLoadingUnits(false)
      }
    }

    setLoadingUnits(true)
    const request = fetchUnitsOfMeasurement()
    unitsRequest = request

    try {
      const fetchedUnits = await request
      unitsCache = fetchedUnits
      setUnits(fetchedUnits)
      return fetchedUnits
    } catch (error) {
      handleApiError(error, modal)
      return []
    } finally {
      unitsRequest = null
      setLoadingUnits(false)
    }
  }

  useEffect(() => {
    if (!open) return
    void ensureUnitsLoaded()
  }, [open])

  useEffect(() => {
    if (!open || !useUnitOfMeasurement) return
    void ensureUnitsLoaded()
  }, [open, useUnitOfMeasurement])

  const close = () => {
    setOpen(false)
    reset(CHARACTERISTIC_CREATION_DEFAULTS)
  }

  const submit = handleSubmit(async (values) => {
    setConfirmLoading(true)
    try {
      const response = await api.post(
        '/main/characteristics',
        toCreateCharacteristicRequest(values, isApprover)
      )

      toast.success('Характеристика создана')
      close()
      onSuccess?.(response.data)
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setConfirmLoading(false)
    }
  })

  return (
    <>
      {contextHolder}
      <AtomModalWithForm
        title="Новая характеристика"
        open={open}
        submitting={confirmLoading}
        onCancel={close}
        onSubmit={submit}
      >
        <AtomFormInput
          layout="stacked"
          location="characteristicCreation"
          idPrefix="characteristic-"
          name="description"
          title="Наименование"
          placeholder="Например, объём бака"
          required
          control={control as FormInputControlType}
          register={register as FormInputRegisterType}
          errors={errors}
        />
        <HoverElement>
          <AtomSelect
            layout="stacked"
            title="Тип значения"
            required
            fullWidth
            openByHover={false}
            formName="valueType"
            formControl={control as SelectFormControlType}
            formErrors={errors}
            options={VALUE_TYPE_OPTIONS}
            popupClassName={cn.selectPopup}
          />
        </HoverElement>
        {hasUnitOption && (
          <Controller
            name="useUnitOfMeasurement"
            control={control}
            render={({ field }) => (
              <AtomCheckbox
                className={cn.checkbox}
                checked={field.value}
                onChange={(checked) => {
                  field.onChange(checked)
                  if (!checked) setValue('unitOfMeasurement', '')
                }}
              >
                Использовать единицу измерения
              </AtomCheckbox>
            )}
          />
        )}
        {hasUnitOption && useUnitOfMeasurement && (
          <HoverElement>
            <AtomSelect
              layout="stacked"
              title="Единица измерения"
              required
              fullWidth
              showSearch
              openByHover={false}
              formName="unitOfMeasurement"
              formControl={control as SelectFormControlType}
              formErrors={errors}
              loading={loadingUnits}
              options={units.map((unit) => ({
                value: unit.id,
                label: unit.description || unit.code,
              }))}
              popupClassName={cn.selectPopup}
            />
          </HoverElement>
        )}
        {isApprover && (
          <Controller
            name="approveImmediately"
            control={control}
            render={({ field }) => (
              <AtomCheckbox
                className={cn.checkbox}
                checked={field.value}
                onChange={field.onChange}
              >
                Сразу утвердить
              </AtomCheckbox>
            )}
          />
        )}
      </AtomModalWithForm>
    </>
  )
}

export default CharacteristicCreationModal
